import React, { useState, useEffect } from 'react';
import PhoneInput from '../shared/PhoneInput';
import sortSelectOptions from '../../utils/sortSelectOptions';
import { TYPE_CONTACT_OPTIONS, ORIGINE_CONTACT_OPTIONS, optionRole } from '../../constants/contactEnums';

const IdentitySection = ({ 
  formData, 
  onChange, 
  userProfile,
  onAddressSearch,
  addressSuggestions,
  showAddressSuggestions,
  onSelectAddress
}) => {
  const [tempTag, setTempTag] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  const [negociateurs, setNegociateurs] = useState([]);

  // Charger les groupes disponibles
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    fetch(`${API_URL}/api/contacts/types/groupes`)
      .then(res => res.json())
      .then(data => {
        // L'API renvoie {groupes: [...], contact_types: [...]}
        setAvailableGroups(data.groupes || []);
      })
      .catch(err => console.error('Erreur chargement groupes:', err));
  }, []);

  // Charger la liste des négociateurs
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    fetch(`${API_URL}/api/negociateurs`)
      .then(res => res.json())
      .then(data => {
        setNegociateurs(data || []);
      })
      .catch(err => console.error('Erreur chargement négociateurs:', err));
  }, []);

  const toggleGroup = (group) => {
    const currentGroups = formData.groupes || [];
    if (currentGroups.includes(group)) {
      onChange('groupes', currentGroups.filter(g => g !== group));
    } else {
      onChange('groupes', [...currentGroups, group]);
    }
  };

  const addTag = () => {
    const currentGroups = formData.groupes || [];
    if (tempTag.trim()) {
      const newTags = tempTag.split(',').map(t => t.trim()).filter(t => t && !currentGroups.includes(t));
      if (newTags.length > 0) {
        // Single source of truth : tout passe par `groupes`
        onChange('groupes', [...currentGroups, ...newTags]);
        setTempTag('');
      }
    }
  };

  const removeTag = (tag) => {
    const currentGroups = formData.groupes || [];
    onChange('groupes', currentGroups.filter(g => g !== tag));
  };

  return (
    <section id="general" className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 md:p-6 scroll-mt-32">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <i className="bi bi-person-fill text-green-600 text-base md:text-lg"></i>
          </div>
          <div>
            <h2 className="text-base md:text-xl font-bold text-slate-800">1. Identité</h2>
            <p className="text-xs md:text-sm text-slate-500">Informations personnelles</p>
          </div>
        </div>
        <button
          type="button"
          className="px-3 md:px-4 py-1.5 md:py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-lg text-xs md:text-sm font-semibold shadow-md transition flex items-center gap-2 w-full sm:w-auto justify-center"
          title="Enrichir le profil avec l'IA"
        >
          <i className="bi bi-stars"></i>
          <span className="hidden sm:inline">Enrichir avec l'IA</span>
          <span className="sm:hidden">Enrichir IA</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Type de contact *</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition text-sm md:text-base"
            value={optionRole(formData.type_contact)?.label || ''}
            onChange={(e) => onChange('type_contact', e.target.value)}
            required
          >
            <option value="">Sélectionner...</option>
            {sortSelectOptions(TYPE_CONTACT_OPTIONS).map(o => (
              <option key={o.value} value={o.label}>{o.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Titre</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition text-sm md:text-base"
            value={formData.titre}
            onChange={(e) => onChange('titre', e.target.value)}
          >
            <option value="">Sélectionner...</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
            <option value="Dr">Dr</option>
            <option value="Me">Me</option>
          </select>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Nom *</label>
          <input 
            type="text" 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition text-sm md:text-base"
            value={formData.nom}
            onChange={(e) => onChange('nom', e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Prénom</label>
          <input 
            type="text" 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition text-sm md:text-base"
            value={formData.prenom}
            onChange={(e) => onChange('prenom', e.target.value)}
          />
        </div>

        {/* Téléphones multiples */}
        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Téléphones</label>
          <div className="space-y-2">
            {(formData.telephones || []).map((tel, index) => (
              <div key={index} className="flex flex-col md:flex-row gap-2">
                <select
                  className="w-full md:w-auto px-3 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none"
                  value={tel.type || 'Mobile'}
                  onChange={(e) => {
                    const newTels = [...formData.telephones];
                    newTels[index] = { ...newTels[index], type: e.target.value };
                    onChange('telephones', newTels);
                  }}
                >
                  <option value="Mobile">Mobile</option>
                  <option value="Fixe">Fixe</option>
                  <option value="Bureau">Bureau</option>
                </select>
                {/* lot DK3 — téléphone : le ✕ reste à droite du numéro, sous le type */}
                <div className="flex gap-2 min-w-0 md:contents">
                <PhoneInput
                  value={tel.numero || ''}
                  onChange={({ e164, national }) => {
                    const newTels = [...formData.telephones];
                    newTels[index] = { ...newTels[index], numero: e164 || national || '' };
                    onChange('telephones', newTels);
                  }}
                  testid={`identity-tel-${index}`}
                  className="flex-1 min-w-0"
                />
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newTels = formData.telephones.filter((_, i) => i !== index);
                      onChange('telephones', newTels);
                    }}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newTels = [...(formData.telephones || []), { type: 'Mobile', numero: '', principal: false }];
                onChange('telephones', newTels);
              }}
              className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-dashed border-green-300 text-green-600 rounded-lg hover:bg-green-50 transition"
            >
              + Ajouter un téléphone
            </button>
          </div>
        </div>

        {/* Emails multiples */}
        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Emails</label>
          <div className="space-y-2">
            {(formData.emails || []).map((email, index) => (
              <div key={index} className="flex flex-col md:flex-row gap-2">
                <select
                  className="w-full md:w-auto px-3 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none"
                  value={email.type || 'Pro'}
                  onChange={(e) => {
                    const newEmails = [...formData.emails];
                    newEmails[index] = { ...newEmails[index], type: e.target.value };
                    onChange('emails', newEmails);
                  }}
                >
                  <option value="Pro">Pro</option>
                  <option value="Perso">Perso</option>
                </select>
                {/* lot DK4 — téléphone : le ✕ reste à droite de l'adresse, sous le type */}
                <div className="flex gap-2 min-w-0 md:contents">
                <input 
                  type="email" 
                  placeholder="Adresse email"
                  className="flex-1 min-w-0 px-4 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
                  value={email.email || ''}
                  onChange={(e) => {
                    const newEmails = [...formData.emails];
                    newEmails[index] = { ...newEmails[index], email: e.target.value };
                    onChange('emails', newEmails);
                  }}
                />
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newEmails = formData.emails.filter((_, i) => i !== index);
                      onChange('emails', newEmails);
                    }}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newEmails = [...(formData.emails || []), { type: 'Pro', email: '', principal: false }];
                onChange('emails', newEmails);
              }}
              className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-dashed border-green-300 text-green-600 rounded-lg hover:bg-green-50 transition"
            >
              + Ajouter un email
            </button>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Société</label>
          <input 
            type="text" 
            placeholder="Nom de la société"
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.nom_societe}
            onChange={(e) => onChange('nom_societe', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Profession</label>
          <input 
            type="text" 
            placeholder="Profession"
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.profession}
            onChange={(e) => onChange('profession', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Origine du contact</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.origine || ''}
            onChange={(e) => onChange('origine', e.target.value)}
          >
            <option value="">Sélectionner une origine...</option>
            {sortSelectOptions(ORIGINE_CONTACT_OPTIONS).map(o => (
              <option key={o.value} value={o.label}>{o.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Négociateur</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.negociateur_id || ''}
            onChange={(e) => onChange('negociateur_id', e.target.value)}
          >
            <option value="">Attribuer à...</option>
            {negociateurs.map((neg) => (
              <option key={neg.id} value={neg.id}>
                {neg.prenom} {neg.nom} {neg.id === (userProfile?.id || userProfile?.userId) ? '(Moi)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Nouveaux champs CRM */}
        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Statut du lead</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.statut_lead || 'Nouveau'}
            onChange={(e) => onChange('statut_lead', e.target.value)}
          >
            <option value="Nouveau">Nouveau</option>
            <option value="Contacté">Contacté</option>
            <option value="Qualifié">Qualifié</option>
            <option value="En négociation">En négociation</option>
            <option value="Converti">Converti</option>
            <option value="Perdu">Perdu</option>
          </select>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Préférence de communication</label>
          <select 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.preference_communication || ''}
            onChange={(e) => onChange('preference_communication', e.target.value)}
          >
            <option value="">Sélectionner...</option>
            <option value="Email">Email</option>
            <option value="Téléphone">Téléphone</option>
            <option value="WhatsApp">WhatsApp</option>
            <option value="SMS">SMS</option>
          </select>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Langues parlées</label>
          <input 
            type="text" 
            placeholder="Ex: Français, Anglais, Italien"
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={Array.isArray(formData.langues) ? formData.langues.join(', ') : formData.langues || ''}
            onChange={(e) => {
              const languesArray = e.target.value.split(',').map(l => l.trim()).filter(l => l);
              onChange('langues', languesArray);
            }}
          />
        </div>

        <div>
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Prochain rappel</label>
          <input 
            type="datetime-local" 
            className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
            value={formData.date_prochain_rappel || ''}
            onChange={(e) => onChange('date_prochain_rappel', e.target.value)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Adresse complète</label>
          <div className="relative">
            <input 
              type="text" 
              placeholder="Commencez à taper une adresse..."
              className="w-full px-3 md:px-4 py-2 md:py-2.5 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition mb-3"
              value={formData.adresse}
              onChange={(e) => {
                onChange('adresse', e.target.value);
                onAddressSearch(e.target.value);
              }}
              onFocus={() => (formData.adresse || '').length >= 3 && onAddressSearch(formData.adresse)}
            />
            {/* Dropdown d'autocomplétion */}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border-2 border-green-500 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                {addressSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-3 hover:bg-green-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                    onClick={() => onSelectAddress(suggestion)}
                  >
                    <div className="font-medium text-slate-800">{suggestion.properties.label}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {suggestion.properties.context}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <input 
              type="text" 
              placeholder="Code postal"
              className="px-4 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
              value={formData.code_postal}
              onChange={(e) => onChange('code_postal', e.target.value)}
            />
            <input 
              type="text" 
              placeholder="Ville"
              className="px-4 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
              value={formData.ville}
              onChange={(e) => onChange('ville', e.target.value)}
            />
            <input 
              type="text" 
              placeholder="Pays"
              className="px-4 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none transition"
              value={formData.pays}
              onChange={(e) => onChange('pays', e.target.value)}
            />
          </div>
        </div>

        {/* Groupes prédéfinis */}
        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Groupes</label>
          <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-200 rounded-lg bg-slate-50">
            {availableGroups.length > 0 ? (
              availableGroups.map((group, idx) => (
                <label
                  key={idx}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition ${
                    (formData.groupes || []).includes(group)
                      ? 'bg-green-500 text-white shadow-md'
                      : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-green-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={(formData.groupes || []).includes(group)}
                    onChange={() => toggleGroup(group)}
                    className="hidden"
                  />
                  <span className="text-sm font-semibold">{group}</span>
                </label>
              ))
            ) : (
              <p className="text-sm text-slate-400 py-2">Chargement des groupes...</p>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs md:text-sm font-semibold text-slate-700 mb-1.5">Tags personnalisés</label>
          <div className="flex flex-wrap gap-2 p-3 border-2 border-slate-200 rounded-lg mb-1.5 min-h-[60px] bg-slate-50">
            {/* Affiche uniquement les groupes hors-grille (= tags personnalisés ajoutés librement) */}
            {(formData.groupes || []).filter(g => !availableGroups.includes(g)).map((tag, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold" data-testid={`contact-tag-${idx}`}>
                {tag}
                <button 
                  type="button" 
                  onClick={() => removeTag(tag)}
                  className="ml-1 text-green-600 hover:text-green-800 font-bold"
                  data-testid={`contact-tag-remove-${idx}`}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </span>
            ))}
            {(formData.groupes || []).filter(g => !availableGroups.includes(g)).length === 0 && (
              <span className="text-xs text-slate-400 italic self-center">Aucun tag personnalisé</span>
            )}
          </div>
          <div className="flex gap-2">
            <input 
              type="text" 
              placeholder="Ajouter des tags séparés par des virgules (ex: VIP, Urgent, Budget important)"
              className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm md:text-base focus:border-green-500 outline-none text-sm"
              value={tempTag}
              onChange={(e) => setTempTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            />
            <button 
              type="button"
              onClick={addTag}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            <i className="bi bi-info-circle"></i>
            Vous pouvez ajouter plusieurs tags en les séparant par des virgules
          </p>
        </div>
      </div>
    </section>
  );
};

export default IdentitySection;
